// src/private/src/pages/app/clients/components/SummaryChips.jsx
import React, { memo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { getMetricTier } from './MetricCell';

const CHIP_METRICS = [
  { key: 'setRate', label: 'Set Rate', format: 'percent' },
  { key: 'issueRate', label: 'Issue Rate', format: 'percent' },
  { key: 'demoRate', label: 'Demo Rate', format: 'percent' },
  { key: 'netCloseRate', label: 'Close Rate', format: 'percent' },
  { key: 'costOfMarketing', label: 'COM', format: 'percent' },
];

// Cost-mix metric is geographically distorted (lead cost varies by market), so
// we deliberately hide the vertical-cohort comparison for it.
const COMPARABLE_KEYS = new Set(['setRate', 'issueRate', 'demoRate', 'netCloseRate']);

const TIER_COLORS = {
  good: { bg: 'rgba(34, 197, 94, 0.12)', border: 'rgba(34, 197, 94, 0.35)', text: '#16a34a' },
  warn: { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.35)', text: '#d97706' },
  bad: { bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.35)', text: '#dc2626' },
  neutral: { bg: 'transparent', border: 'rgba(0,0,0,0.12)', text: 'text.secondary' },
};

const formatPct = (val) => {
  const num = Number(val);
  if (!Number.isFinite(num)) return '--';
  return `${(num * 100).toFixed(1)}%`;
};

/**
 * Horizontal row of colored chips showing key funnel conversion rates.
 * Each chip is colored based on the conditional thresholds.
 *
 * `comparison` is an optional object with the same metric keys whose values
 * represent the vertical-peer benchmark for the same period. When present,
 * a small "vs vertical: X%" subtitle is rendered beneath the client's value.
 */
function SummaryChips({ metrics = {}, comparison = null }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ mt: 2, mb: 1 }}
    >
      {CHIP_METRICS.map((m) => {
        const value = metrics[m.key];
        const tier = getMetricTier(m.key, value);
        const colors = TIER_COLORS[tier] || TIER_COLORS.neutral;
        const peerValue = comparison && COMPARABLE_KEYS.has(m.key) ? comparison[m.key] : null;
        const showPeer = peerValue !== null && peerValue !== undefined && Number.isFinite(Number(peerValue));
        const delta = showPeer && Number.isFinite(Number(value))
          ? Number(value) - Number(peerValue)
          : null;

        const chipContent = (
          <Box
            sx={{
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.25,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: colors.border,
              bgcolor: colors.bg,
              minWidth: showPeer ? 110 : 'auto',
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                variant="caption"
                sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 500 }}
              >
                {m.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: '0.8rem', fontWeight: 700, color: colors.text }}
              >
                {formatPct(value)}
              </Typography>
            </Box>
            {showPeer && (
              <Typography
                variant="caption"
                sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.1 }}
              >
                vertical avg: {formatPct(peerValue)}
                {delta !== null && Number.isFinite(delta) && Math.abs(delta) >= 0.0005 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      fontWeight: 700,
                      color: delta >= 0 ? '#16a34a' : '#dc2626',
                    }}
                  >
                    {delta >= 0 ? '+' : ''}
                    {(delta * 100).toFixed(1)}pt
                  </Box>
                )}
              </Typography>
            )}
          </Box>
        );

        if (!showPeer) {
          return <React.Fragment key={m.key}>{chipContent}</React.Fragment>;
        }

        const metricN = Number(comparison?.[`${m.key}_n`]) || 0;
        const tooltipBody = metricN
          ? `Average across ${metricN} Guru ${metricN === 1 ? 'client' : 'clients'} in your vertical reporting this metric for the selected period. Clients with no data for this funnel step are excluded.`
          : 'Average across other Guru clients in your vertical reporting this metric for the selected period.';
        return (
          <Tooltip key={m.key} title={tooltipBody} arrow>
            {chipContent}
          </Tooltip>
        );
      })}
    </Stack>
  );
}

export default memo(SummaryChips);
